/**
 * External Imports
 */
import { FC, useMemo, useEffect, useState, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { format, differenceInDays } from 'date-fns';

/**
 * Internal Imports
 */
import { MarkCompleteBtn } from '../../../shared/buttons';
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';
import {
  openModal,
  setModalData,
  setModalType,
  clearIssueFilter,
  setTaskSort,
  setUserSection
} from '../../../../redux/actions';

import { useUserRolesRef } from '../../../../services/UserRoles';

import { useIssuesApi } from '../../../../utils/api';
import { useDebounceValue } from '../../../../utils/debouncers';
import { date, abbreviatedName } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  EmpDepartment,
  Filter,
  Issue
} from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CategoryContainer,
  CategoryLabel,
  CategoryText,
  CategoryIndent,
  CellContainer,
  ServiceCentralLogo,
  StyledLabel,
  StyledNeuTableRow,
  TextContainer
} from '../../../shared/table/Table.styles';

/**
 * Static File Imports
 */
import ServiceCentralIcon from '../../../../assets/images/service_central_icon.png';

/**
 * Constant Imports
 */
import serviceCentralCategories from '../../../../constants/serviceCentralCategories.json';

interface IIssueTableProps {
  authorizedUser: AuthUserProps;
  currentIssuePage: number;
  issueFilter: Filter;
  loadingIssuesByFacility: boolean;
  loadingIssueListByUnitsOrEmpList: boolean;
  location: string;
  searchTerm: string;
  selectedDelegate: string;
  selectedUnits: { id: string; unit: string }[];
  storeIssues: Issue[];
  totalCount: number;
  units: { unitId: string; unit: string }[];
  selectedEmpDepartments: EmpDepartment[];
}

const IssuesTable: FC<IIssueTableProps> = ({
  authorizedUser,
  currentIssuePage,
  issueFilter,
  loadingIssuesByFacility,
  loadingIssueListByUnitsOrEmpList,
  location,
  searchTerm,
  selectedDelegate,
  selectedUnits,
  storeIssues,
  totalCount,
  units,
  selectedEmpDepartments
}) => {
  const dispatch = useDispatch();

  const [sortKey, setSortKey] = useState<string>('created');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [active, setActive] = useState<string>('Created');
  const [loading, setLoading] = useState<boolean>(true);

  const { from = '' } = useLocation().state ?? {};

  const { isEnterpriseAdmin } = useUserRolesRef();

  const dbSearchTerm = useDebounceValue(searchTerm.trim(), 350);

  const fetchIssues = useCallback(async () => {
    if (from) dispatch(setUserSection(from.split('/')[0]));
    setLoading(true);
    const issuesAction = await useIssuesApi({
      authorizedUser,
      currentIssuePage,
      issueFilter,
      searchTerm: dbSearchTerm && dbSearchTerm.length > 2 ? dbSearchTerm : '',
      selectedDelegate,
      selectedUnits,
      selectedEmpDepartments,
      sortDir,
      sortKey,
      summaryView: from ? from.split('/')[0] : undefined
    });
    dispatch(issuesAction);
    setLoading(false);
  }, [
    authorizedUser,
    currentIssuePage,
    dbSearchTerm,
    from,
    issueFilter,
    selectedDelegate,
    selectedUnits,
    selectedEmpDepartments,
    sortDir,
    sortKey
  ]);

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Created':
        key = 'created';
        break;
      case 'Department':
      case 'Location':
        key = 'location';
        break;
      case 'Category/Subcategory':
        key = 'category';
        break;
      case 'Patient':
        key = 'patientName';
        break;
      case 'Employee':
        key = 'employeeName';
        break;
      case 'Last Comment':
        key = 'lastComment';
        break;
      case 'Attached':
        key = 'attachedTo';
        break;
      case 'Status':
        key = 'status';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openIssue = (issue: Issue) => {
    if (
      serviceCentralCategories.includes(issue.category) ||
      (issue.taskSubtype &&
        serviceCentralCategories.includes(issue.taskSubtype))
    ) {
      dispatch(setModalType('View Issue'));
    } else if (
      isEnterpriseAdmin ||
      (issue.userId.toLowerCase() === authorizedUser.hcaid.toLowerCase() &&
        !issue.completed &&
        issue.taskDate &&
        !date.isOverOneDayOld(issue.taskDate))
    ) {
      dispatch(setModalType('Edit Issue'));
    } else {
      dispatch(setModalType('Edit Issue'));
    }
    dispatch(setModalData(issue));
    dispatch(openModal());
  };

  const issues = useMemo(() => {
    let iss = storeIssues;
    if (location === 'patient') {
      iss = storeIssues.map((issue: Issue) => ({
        ...issue,
        unit: units.find(unit => unit.unitId === issue.unitId)?.unit
      }));
    }
    return iss;
  }, [storeIssues, selectedEmpDepartments]);

  const isTableEmpty = useMemo(
    () => !dbSearchTerm && storeIssues.length === 0,
    [dbSearchTerm, storeIssues]
  );

  const isTableEmptySearch = useMemo(
    () => dbSearchTerm && storeIssues.length === 0,
    [dbSearchTerm, storeIssues]
  );

  // Reset page when navigate away
  useEffect(() => {
    return () => {
      dispatch(clearIssueFilter());
      dispatch(setTaskSort({ sortKey: 'created', sortDir: 'desc' }));
    };
  }, []);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  useEffect(() => {
    dispatch(setTaskSort({ sortKey, sortDir }));
  }, [sortDir, sortKey]);

  const headers: JSX.Element[] = [
    ...[
      <CellContainer
        key="Created"
        id="Issue-Table-Created-Column"
        unit="column"
        colWidth={11}
        direction={!(active === 'Created') ? 'none' : sortDir}
        sort
      >
        Created
      </CellContainer>,
      <CellContainer
        key="Location"
        id="Issue-Table-Location-Column"
        unit="column"
        colWidth={location === 'employee' ? 12 : 14}
        direction={
          !(active === 'Location' || active === 'Department') ? 'none' : sortDir
        }
        sort
      >
        Location
        {/* TODO: This isnt working with Neutron */}
        {/* {location === 'patient' ? 'Location' : 'Department'} */}
      </CellContainer>,
      <CellContainer
        key="Issue Category"
        id="Issue-Table-Category-Column"
        unit="column"
        colWidth={location === 'csc' || location === 'cned' ? 17 : 14}
        direction={!(active === 'Category/Subcategory') ? 'none' : sortDir}
        sort
        style={{ marginLeft: '20px' }}
      >
        Category/Subcategory
      </CellContainer>,
      <CellContainer
        key="Description"
        id="Issue-Table-Description-Column"
        unit="column"
        colWidth={location === 'csc' || location === 'cned' ? 18 : 15}
        direction="none"
      >
        Description
      </CellContainer>
    ],
    ...(location === 'patient'
      ? [
          <CellContainer
            key="Patient"
            id="Issue-Table-Patient-Column"
            unit="column"
            colWidth={7}
            direction={!(active === 'Patient') ? 'none' : sortDir}
            sort
          >
            Patient
          </CellContainer>
        ]
      : [
          <CellContainer
            key="Employee"
            id="Issue-Table-Employee-Column"
            unit="column"
            colWidth={9}
            direction={!(active === 'Employee') ? 'none' : sortDir}
            sort
          >
            Employee
          </CellContainer>
        ]),
    <CellContainer
      key="Last Comment"
      id="Issue-Table-Comment-Column"
      unit="column"
      colWidth={16}
      direction={!(active === 'Last Comment') ? 'none' : sortDir}
      sort
    >
      Last Comment
    </CellContainer>,
    ...(!(location === 'csc' || location === 'cned')
      ? [
          <CellContainer
            key="Attached to"
            id="Issue-Table-Attached-Column"
            unit="column"
            colWidth={8}
            direction={!(active === 'Attached') ? 'none' : sortDir}
            sort
          >
            Attached
          </CellContainer>
        ]
      : []),
    <CellContainer
      key="Status"
      id="Issue-Table-Status-Column"
      unit="column"
      colWidth={15}
      direction={!(active === 'Status') ? 'none' : sortDir}
      sort
    >
      Status
    </CellContainer>
  ];

  const rows = issues.map((issue: Issue, i: number) => (
    <StyledNeuTableRow
      // eslint-disable-next-line react/no-array-index-key
      key={`issue-row-${i}`}
      data-testid={`issue-row-${issue.taskId}`}
      unit="row"
      size="default"
      height="8em"
      role="button"
      styles={issue.completed ? 'background-color: #f6f6f6 !important' : ''}
      onClick={() => openIssue(issue)}
    >
      <CellContainer unit="column" colWidth={11} alignment="start">
        <StyledLabel className="bold">{issue.friendlyId}</StyledLabel>
        <StyledLabel
          className="neu-text-gray-90 bold"
          style={{ fontWeight: 600 }}
        >
          {issue.userFullName}
        </StyledLabel>
        <StyledLabel>
          {issue.taskDate ? date.dayOfYrFull(issue.taskDate) : ''}
        </StyledLabel>
        <StyledLabel>
          {!issue.completed &&
            (issue.taskDate && date.isOrbitTimestamp(issue.taskDate)
              ? `${differenceInDays(
                  new Date(),
                  date.parseDate(issue.taskDate)
                )}d`
              : '')}
        </StyledLabel>
      </CellContainer>
      <CellContainer
        unit="column"
        colWidth={location === 'employee' ? 12 : 14}
        alignment="start"
      >
        <StyledLabel className="neu-text-gray-90 bold">
          {location === 'patient'
            ? issue.unit
            : issue.department
            ? issue.department
            : 'Unknown'}
        </StyledLabel>
        <StyledLabel>
          {location === 'patient' &&
            (issue.room ? `${issue.room}${issue.bed || ''}` : '')}
        </StyledLabel>
      </CellContainer>
      <CellContainer
        unit="column"
        colWidth={location === 'csc' || location === 'cned' ? 17 : 14}
        alignment="start"
      >
        <CategoryContainer>
          <CategoryLabel className="neu-text-gray-90">
            {serviceCentralCategories.includes(issue.category) ||
            serviceCentralCategories.includes(issue.taskSubtype) ? (
              <ServiceCentralLogo
                width="25"
                alt="service central logo"
                src={ServiceCentralIcon}
              />
            ) : (
              <CategoryIndent />
            )}
          </CategoryLabel>
          <TextContainer>
            <CategoryText>{issue.category || issue.taskSubtype}</CategoryText>
            <StyledLabel>{issue.subCategory}</StyledLabel>
          </TextContainer>
        </CategoryContainer>
      </CellContainer>
      <CellContainer
        unit="column"
        colWidth={location === 'csc' || location === 'cned' ? 18 : 15}
        alignment="start"
      >
        <StyledLabel className="neu-text-gray-90 text-clamp text-clamp__3">
          {issue.description}
        </StyledLabel>
      </CellContainer>
      <CellContainer
        unit="column"
        colWidth={location === 'patient' ? 7 : 9}
        alignment="start"
      >
        <StyledLabel className="neu-text-gray-90 bold">
          {location === 'patient'
            ? issue.patientFullName === ' ' || null
              ? issue.patientFullName
              : abbreviatedName(issue.patientFirstName, issue.patientLastName)
            : issue.employeeFullName}
        </StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={16} alignment="start">
        <div>
          {issue.latestComment ? (
            <>
              <StyledLabel className="neu-text-gray-90">
                {issue.latestComment.date &&
                date.isOrbitTimestamp(issue.latestComment.date) &&
                issue.latestComment.authorFullName
                  ? `${format(
                      date.parseDate(issue.latestComment.date),
                      'MM/dd/yyyy'
                    )} | ${issue.latestComment.authorFullName}`
                  : ''}
              </StyledLabel>
              <StyledLabel className="neu-text-gray-90 text-clamp text-clamp__3">
                {issue.latestComment.body ? issue.latestComment.body : ''}
              </StyledLabel>
            </>
          ) : (
            <StyledLabel>No Comments</StyledLabel>
          )}
        </div>
      </CellContainer>
      {!(location === 'csc' || location === 'cned') && (
        <CellContainer unit="column" colWidth={8} alignment="start">
          <StyledLabel className="neu-text-blue-60 bold">
            {issue.stoplightId ? 'Stoplight' : null}
          </StyledLabel>
        </CellContainer>
      )}
      <CellContainer unit="column" colWidth={15} alignment="start">
        <StyledLabel className="neu-text-gray-90 mb-2 bold">
          {issue.completed ? 'Completed' : 'Opened'}
        </StyledLabel>
        <StyledLabel>
          {issue.completed &&
            issue.completedDate &&
            date.isOrbitTimestamp(issue.completedDate) &&
            format(date.parseDate(issue.completedDate), 'MM-dd-yyyy')}
        </StyledLabel>
        <StyledLabel>
          {!issue.completedDate &&
          !(
            serviceCentralCategories.includes(issue.category) ||
            serviceCentralCategories.includes(issue.taskSubtype)
          ) ? (
            <MarkCompleteBtn taskId={issue.taskId} taskType="issue" />
          ) : (
            ''
          )}
        </StyledLabel>
      </CellContainer>
    </StyledNeuTableRow>
  ));

  return (
    <Table
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={isTableEmptySearch}
      loading={
        loading || loadingIssuesByFacility || loadingIssueListByUnitsOrEmpList
      }
      totalResults={totalCount}
      type="issue"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authorizedUser: state.AuthorizedUser?.authorizedUser,
  currentIssuePage: state.TaskReducer.currentIssuePage,
  issueFilter: state.TaskReducer.issueFilter,
  loadingIssueListByUnitsOrEmpList:
    state.TaskReducer.loadingIssuesByUnitIdsOrEmployeeList,
  loadingIssuesByFacility: state.TaskReducer.loadingIssuesByFacility,
  location: state.UserReducer.userSection,
  selectedDelegate: state.EmployeeReducer.selectedDelegatedEmployee.hcaid,
  selectedUnits: state.ConfigReducer.selectedUnits,
  storeIssues: state.TaskReducer.issues,
  totalCount: state.TaskReducer.totalCount,
  units: state.ConfigReducer.units,
  selectedEmpDepartments: state.ConfigReducer.selectedEmpDepartments,
  allEmpDepts: state.ConfigReducer.empDepartments
});

export default connect(mapReduxStateToProps)(IssuesTable);
